import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type UserOrderResponseWithFlagsSubOrdersInner } from '@redteclab/api/clients/bully'

import { type OpenableControls } from '../../../../generic-hooks'
import { Loading } from '../../../../loading'
import { userSubOrderAccessorGetSubOrderProducts } from '../../../../order'
import { useDirectContactFormAvailableOrders } from '../../../hooks/useDirectContactFormAvailableOrders'
import { useDirectContactFormMiraklReturnAvailability } from '../../../hooks/useDirectContactFormMiraklReturnAvailability'
import { useDirectContactFormOrderAuthenticationError } from '../../../hooks/useDirectContactFormOrderAuthenticationError'

import { DirectContactFormOrderProductItemsSelection } from './DirectContactFormOrderProductItemsSelection/DirectContactFormOrderProductItemsSelection'
import { DirectContactFormOrderProductsSelectionProvider } from './DirectContactFormOrderProductsSelectionContext'
import { DirectContactFormOrderProductsSelectionEmpty } from './DirectContactFormOrderProductsSelectionEmpty'
import { DirectContactFormOrderProductsSelectionList } from './DirectContactFormOrderProductsSelectionList'

export const DirectContactFormOrderProductsSelectionContent: FC<{
  modalControls: OpenableControls
  onSelectSubOrder: (
    subOrder: UserOrderResponseWithFlagsSubOrdersInner | null,
  ) => void
  selectedSubOrder: UserOrderResponseWithFlagsSubOrdersInner | null
}> = ({ modalControls, onSelectSubOrder, selectedSubOrder }) => {
  const {
    availableOrders,
    error,
    hasMore,
    isLoading,
    isLoadingMore,
    onLoadMore,
  } = useDirectContactFormAvailableOrders()
  const {
    availableOrderIds: miraklReturnAvailableOrderIds,
    isLoading: isMiraklReturnAvailabilityLoading,
  } = useDirectContactFormMiraklReturnAvailability(availableOrders)
  const isAuthenticationError = useDirectContactFormOrderAuthenticationError({
    error,
    modalControls,
  })

  if (selectedSubOrder) {
    return (
      <DirectContactFormOrderProductItemsSelection
        modalControls={modalControls}
        onBack={() => {
          onSelectSubOrder(null)
        }}
        products={userSubOrderAccessorGetSubOrderProducts(selectedSubOrder)}
      />
    )
  }

  if (isLoading) {
    return <Loading />
  }

  if (isAuthenticationError) {
    return null
  }

  if (error) {
    return (
      <p className="text-dark-error" role="alert">
        <FormattedMessage id="general.error" />
      </p>
    )
  }

  if (!hasMore && availableOrders.length === 0) {
    return (
      <DirectContactFormOrderProductsSelectionEmpty
        onClose={modalControls.handleClose}
      />
    )
  }

  return (
    <DirectContactFormOrderProductsSelectionProvider
      isMiraklReturnAvailabilityLoading={isMiraklReturnAvailabilityLoading}
      onClose={modalControls.handleClose}
      onSelectProducts={onSelectSubOrder}
    >
      <DirectContactFormOrderProductsSelectionList
        hasMore={hasMore}
        isLoading={isLoadingMore || isMiraklReturnAvailabilityLoading}
        miraklReturnAvailableOrderIds={miraklReturnAvailableOrderIds}
        onLoadMore={onLoadMore}
        orders={availableOrders}
      />
    </DirectContactFormOrderProductsSelectionProvider>
  )
}
